// Requisitos para que una definición se pueda ejecutar, comprobados en vivo mientras se edita (los
// mismos que valida el servidor al guardar: disparador, formularios fuente, su clave y que cada
// conexión vaya a un campo válido de la entidad), más los avisos que no impiden ejecutar.
import { entityByClass, extensionsOf, formById, formName, keyRoleOf, shortName } from '@vem/catalog.js';

const CRON_FIELD = /^[0-9A-Za-z*?#,/-]+$/;

/** Cron de Spring: 6 campos (segundo minuto hora día mes día-semana). */
export const cronValid = (expr) => {
  const parts = String(expr || '').trim().split(/\s+/);
  return parts.length === 6 && parts.every((p) => CRON_FIELD.test(p));
};

function triggerValid(d) {
  if (d.triggerType === 'cron') return cronValid(d.triggerCron);
  if (d.triggerType === 'interval') return Number.isInteger(d.triggerEvery) && d.triggerEvery >= 1;
  return !!d.triggerType;
}

/** Conexiones que el servidor rechazaría: a una clase que no es la raíz ni su extensión, a un campo inexistente o a la clave. */
function badTargets(d) {
  const classes = d.rootEntity ? [d.rootEntity, ...extensionsOf(d.rootEntity)] : [];
  const seen = new Set();
  return d.maps.filter((m) => {
    const fields = entityByClass(m.entityClass)?.fields || [];
    return !classes.includes(m.entityClass) || !fields.some((f) => f.name === m.fieldName)
      || !!keyRoleOf(d.rootEntity, m.entityClass, m.fieldName);
  }).map((m) => `${m.entityClass}#${m.fieldName}`).filter((k) => !seen.has(k) && seen.add(k));
}

/**
 * Lista de requisitos de una definición.
 *
 * @param {object} d definición en edición (dto del editor)
 * @returns {Array<{key:string, ok:boolean, label:string, missing?:string[], target?:object}>}
 *   `missing` = dónde falta; `target` = dónde se arregla
 */
export function readiness(d) {
  const active = d.sources.filter((s) => s.enabled);
  const noId = active.filter((s) => !s.idAlias);
  const noOrigin = active.filter((s) => s.keyMode !== 'entity' && !s.originAlias && !(s.originText || '').trim());
  const formTarget = (list, focus) => (list.length ? { kind: 'form', id: list[0].questId, focus } : null);
  const bad = badTargets(d);
  return [
    { key: 'name', ok: !!d.name.trim(), label: 'chk.name', target: { kind: 'definition', focus: 'name' } },
    { key: 'entity', ok: !!d.rootEntity, label: 'chk.entity', target: { kind: 'entity' } },
    { key: 'trigger', ok: triggerValid(d), label: 'chk.trigger', target: { kind: 'trigger' } },
    { key: 'sources', ok: active.length > 0, label: 'chk.sources', target: { kind: 'addSource' } },
    { key: 'idAlias', ok: active.length > 0 && !noId.length, label: 'chk.idAlias', missing: noId.map((s) => formName(s.questId)), target: formTarget(noId, 'idAlias') },
    { key: 'origin', ok: active.length > 0 && !noOrigin.length, label: 'chk.origin', missing: noOrigin.map((s) => formName(s.questId)), target: formTarget(noOrigin, 'origin') },
    { key: 'targets', ok: !bad.length, label: 'chk.targets', missing: bad.map((k) => k.split('#').map((x, i) => (i ? x : shortName(x))).join('.')),
      target: bad.length ? { kind: 'field', id: bad[0] } : null },
    { key: 'fields', ok: d.maps.length > 0, label: 'chk.fields', target: { kind: 'canvas' } },
  ];
}

// Grupos de avisos de las recetas (código de la frase → grupo de la lista).
const GROUPS = [
  ['dead', ['w.dead', 'rc.src.dead']],
  ['sysType', ['w.sysType', 'w.sysTypeDate']],
  ['unsupported', ['rc.conv.unsupported']],
  ['clearSilent', ['w.clearSilent1', 'w.clearSilentN']],
  ['concatNotText', ['w.concatNotText']],
  ['prioFlat', ['rc.rule.prio.flat']],
  ['afterSys', ['w.afterSys']],
];

/**
 * Avisos de configuración: no impiden ejecutar, pero algo no hará lo que parece.
 *
 * @param {object} d definición en edición
 * @param {Map<string, object>} recipes receta por campo (targetKey)
 * @returns {Array<{key:string, level:'warn'|'danger', label:string, missing:string[], target:object}>}
 */
export function advisories(d, recipes) {
  const out = [];
  for (const [key, codes] of GROUPS) {
    const ids = [];
    recipes.forEach((r, id) => {
      if ((r.warnings || []).some((w) => codes.includes(w.code))) ids.push(id);
    });
    if (ids.length) {
      out.push({ key, level: key === 'sysType' || key === 'unsupported' ? 'danger' : 'warn', label: 'adv.' + key,
        missing: ids.map((id) => id.split('#')[1]), target: { kind: 'field', id: ids[0] } });
    }
  }
  const gone = d.sources.filter((s) => s.enabled && !formById(s.questId));
  if (gone.length) {
    out.push({ key: 'formMissing', level: 'warn', label: 'adv.formMissing', missing: gone.map((s) => s.questId),
      target: { kind: 'form', id: gone[0].questId } });
  }
  return out;
}
